import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Checkbox,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { describeRuleKey } from "encamp-shared/src/waste/fieldIssueRuleKeys";
import { BulkManifestDiscrepanciesQuery } from "generated-graphql/graphql";
import React from "react";

import {
  getDiscrepancyStatus,
  getStatusChipIcon,
  getStatusChipTooltip,
  getStatusColor,
  getStatusLabel,
} from "./DiscrepancyReviewDialog.helpers";
import { SECTION_TITLE_HEADER_HEIGHT } from "./DiscrepancySection";

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

const COL_COUNT = 8; // checkbox, Facility, Manifest, Issue, Source(s), Value, Status, actions

function parseStoredFieldIssueValues(value: string | null | undefined): string[] {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === "[]") return [];

  // Both writers emit comma-joined text, so that is the default reading. A few
  // rows are JSON instead, and those take the parsed shape when it applies.
  let parts = trimmed.split(",");
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (Array.isArray(parsed)) parts = parsed.map((item) => String(item));
    else if (typeof parsed === "string") parts = [parsed];
  } catch {
    // Bare codes are not valid JSON; the comma split already covers them.
  }

  return parts.map((item) => item.trim()).filter((item) => item.length > 0);
}

function getFieldIssueValueText(d: Discrepancy): string {
  const values = [d.eManifestValue, d.vendorValue, d.manifestDocumentValue];
  const uniqueValues = new Set<string>();

  for (const value of values) {
    for (const parsedValue of parseStoredFieldIssueValues(value)) {
      uniqueValues.add(parsedValue);
    }
  }

  return uniqueValues.size > 0 ? [...uniqueValues].join(", ") : "—";
}

export interface FieldIssuesPaneProps {
  items: Discrepancy[];
  loading?: boolean;
  /** Selection set shared with the dialog's sticky bulk-action bar. */
  selectedIds: ReadonlySet<string>;
  /** Toggle a row's selection; shiftKey enables range-select like the field tab. */
  onToggleSelect: (id: string, opts?: { shiftKey?: boolean }) => void;
  onRowMenuOpen: (params: { anchorEl: HTMLElement; discrepancy: Discrepancy }) => void;
}

function FieldIssuesLoadingSkeleton() {
  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {["", "Facility", "Manifest", "Issue", "Source(s)", "Value", "Status", ""].map(
              (h, i) => (
                <TableCell key={h || `sk-${i}`}>
                  <Skeleton variant="text" width={80} />
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 6 }, (_, i) => (
            <TableRow key={i}>
              {Array.from({ length: COL_COUNT }, (__, j) => (
                <TableCell key={j}>
                  <Skeleton variant="text" width={j === 1 ? 120 : 80} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

function FieldIssuesHeader() {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" />
        <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Facility</TableCell>
        <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Manifest</TableCell>
        <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>Issue</TableCell>
        <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Source(s)</TableCell>
        <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>Value</TableCell>
        <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Status</TableCell>
        <TableCell padding="checkbox" />
      </TableRow>
    </TableHead>
  );
}

function SelectionCell({
  discrepancy,
  selected,
  onToggleSelect,
}: {
  discrepancy: Discrepancy;
  selected: boolean;
  onToggleSelect: FieldIssuesPaneProps["onToggleSelect"];
}) {
  return (
    <TableCell padding="checkbox">
      <Checkbox
        size="small"
        checked={selected}
        onChange={(e) => {
          // shiftKey lives on the native event, not the synthetic ChangeEvent.
          const nativeEvt = e.nativeEvent;
          const shiftKey =
            nativeEvt instanceof MouseEvent || nativeEvt instanceof KeyboardEvent
              ? nativeEvt.shiftKey
              : false;
          onToggleSelect(discrepancy.id, { shiftKey });
        }}
        // Matches the Data Source Conflicts grid's top-aligned checkbox
        // (measured in-browser: 6px lands on the first text line).
        sx={{ mt: "6px" }}
      />
    </TableCell>
  );
}

function StatusCell({ discrepancy }: { discrepancy: Discrepancy }) {
  const status = getDiscrepancyStatus(discrepancy);
  const tooltipText = getStatusChipTooltip(discrepancy, status);
  const statusIcon = getStatusChipIcon(discrepancy, status);
  const chip = (
    <Chip
      size="small"
      label={getStatusLabel(status)}
      color={getStatusColor(status)}
      variant="outlined"
      icon={statusIcon ? React.cloneElement(statusIcon, { "aria-hidden": true }) : undefined}
      sx={{
        cursor: "default",
        mt: "2px",
        flexDirection: "row-reverse",
        bgcolor: "background.paper",
        "& .MuiChip-icon": {
          fontSize: 14,
          opacity: 0.7,
          marginRight: "5px",
          marginLeft: "-2px",
        },
      }}
    />
  );

  return (
    <TableCell>{tooltipText ? <Tooltip title={tooltipText}>{chip}</Tooltip> : chip}</TableCell>
  );
}

function FieldIssueRow({
  discrepancy,
  selected,
  onToggleSelect,
  onRowMenuOpen,
}: {
  discrepancy: Discrepancy;
  selected: boolean;
  onToggleSelect: FieldIssuesPaneProps["onToggleSelect"];
  onRowMenuOpen: FieldIssuesPaneProps["onRowMenuOpen"];
}) {
  const ruleKey = discrepancy.ruleKey ?? null;
  const issueDesc = ruleKey != null ? describeRuleKey(ruleKey) : "—";

  return (
    <TableRow hover selected={selected}>
      <SelectionCell
        discrepancy={discrepancy}
        selected={selected}
        onToggleSelect={onToggleSelect}
      />
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Typography variant="body2" sx={{ pt: "4px" }}>
          {discrepancy.facilityName ?? "—"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {discrepancy.epaId}
        </Typography>
      </TableCell>

      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Typography variant="body2" fontWeight={500} sx={{ pt: "4px" }}>
          {discrepancy.manifestTrackingNumber ?? discrepancy.processedManifestId.slice(0, 8)}
        </Typography>
        {discrepancy.wasteLineUnifiedId && (
          <Typography variant="caption" color="text.secondary">
            Line {discrepancy.wasteLineNumber ?? "item"}
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ maxWidth: 280, wordBreak: "break-word" }}>
        <Typography variant="body2" sx={{ pt: "4px" }}>
          {issueDesc}
        </Typography>
      </TableCell>

      <TableCell>
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ pt: "4px" }}>
          <Chip
            size="small"
            label="Encamp"
            variant="outlined"
            sx={{ fontSize: "0.7rem", height: 20 }}
          />
        </Stack>
      </TableCell>

      <TableCell sx={{ maxWidth: 220, wordBreak: "break-word" }}>
        <Typography variant="body2" sx={{ pt: "4px" }}>
          {getFieldIssueValueText(discrepancy)}
        </Typography>
      </TableCell>

      <StatusCell discrepancy={discrepancy} />

      <TableCell padding="checkbox">
        <Tooltip title="Row actions">
          <IconButton
            size="small"
            sx={{ mt: "6px" }}
            onClick={(event) =>
              onRowMenuOpen({ anchorEl: event.currentTarget, discrepancy: discrepancy })
            }
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function FieldIssuesEmptyRow({ subtleSurfaceBg }: { subtleSurfaceBg: string }) {
  return (
    <TableRow>
      <TableCell
        colSpan={COL_COUNT}
        align="center"
        sx={{ py: 8, bgcolor: subtleSurfaceBg, borderBottom: "none" }}
      >
        <Typography variant="body2" color="text.secondary">
          No field issues found for the selected manifest(s).
        </Typography>
      </TableCell>
    </TableRow>
  );
}

export function FieldIssuesPane({
  items,
  loading,
  selectedIds,
  onToggleSelect,
  onRowMenuOpen,
}: FieldIssuesPaneProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const subtleSurfaceBg = isDark ? theme.palette.grey[900] : theme.palette.grey[50];
  const stickyOpaquePanelBg = isDark
    ? {
        backgroundColor: theme.palette.background.paper,
        backgroundImage: `linear-gradient(${theme.palette.common.white}29, ${theme.palette.common.white}29)`,
      }
    : { backgroundColor: theme.palette.background.paper };

  if (loading && items.length === 0) {
    return <FieldIssuesLoadingSkeleton />;
  }

  return (
    // White grid surface so rows don't inherit the section card's gray; the grid is the
    // card's last element, so round its bottom to match the card. "inherit" needs the
    // container to stay the section Paper's direct child — don't rewrap it.
    <TableContainer
      sx={{
        overflow: "visible",
        bgcolor: "background.paper",
        borderBottomLeftRadius: "inherit",
        borderBottomRightRadius: "inherit",
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          "& .MuiTableHead-root .MuiTableCell-head": {
            // Stack below the section's sticky title band.
            top: SECTION_TITLE_HEADER_HEIGHT,
            ...stickyOpaquePanelBg,
            borderBottom: `1px solid ${theme.palette.background.paper}`,
          },
          "& .MuiTableBody-root .MuiTableCell-body": {
            verticalAlign: "top",
          },
        }}
      >
        <FieldIssuesHeader />

        <TableBody>
          {items.map((d) => (
            <FieldIssueRow
              key={d.id}
              discrepancy={d}
              selected={selectedIds.has(d.id)}
              onToggleSelect={onToggleSelect}
              onRowMenuOpen={onRowMenuOpen}
            />
          ))}

          {items.length === 0 && <FieldIssuesEmptyRow subtleSurfaceBg={subtleSurfaceBg} />}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
